import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { formatMessageTime } from './messageTime';
import { useSubmitMessageFeedbackMutation } from '@/redux/internal-services/agentApi';
import { IconButton } from '@/components/UI/IconButton';
import { CheckIcon, CopyIcon } from '@/components/UI/icons';

const COPY_ENABLED = false;

export interface TextMessage {
  kind: 'text';
  id: string;
  role: 'user' | 'assistant';
  text: string;
  streaming?: boolean;
  /** Epoch ms the bubble was created (or its server created_at when restored from history). */
  createdAt?: number;
  /** Live realtime-voice turn — its bubble id is a throwaway local id, so the agent-steps
   *  inspector targets the session's run via `runId` instead of the bubble id. */
  voice?: boolean;
  /** conversation_runs run_id of the realtime session this bubble belongs to. Each start→stop is
   *  its own run, so the steps viewer fetches only this session's trace (not the whole conversation). */
  runId?: string;
  /** Attachments sent with this (user) turn — rendered as file chips under the bubble text. */
  files?: { name: string; type?: string; url?: string }[];
  /** Outbound delivery state for a user turn. Absent/'sent' renders normally; 'sending' and
   *  'failed' render a status line so a lost/slow send is visible instead of silently vanishing. */
  deliveryStatus?: 'sending' | 'sent' | 'failed';
}

type FeedbackState = 'idle' | 'form' | 'submitted';

export const MessageBubble = ({
  msg,
  onRetry,
  feedbackEnabled = false,
  conversationId,
}: {
  msg: TextMessage;
  onRetry?: () => void;
  feedbackEnabled?: boolean;
  conversationId?: string;
}) => {
  const { t, i18n } = useTranslation();
  const [feedbackState, setFeedbackState] = useState<FeedbackState>('idle');
  const [helpful, setHelpful] = useState<boolean | null>(null);
  const [comment, setComment] = useState('');
  const [submitFeedback, { isLoading: submitting }] = useSubmitMessageFeedbackMutation();

  const [copied, setCopied] = useState(false);

  const showFeedback = feedbackEnabled && msg.role === 'assistant' && !msg.streaming;
  const showTime = msg.createdAt != null && !msg.streaming && msg.deliveryStatus !== 'failed';
  // The copy button is hidden for now; flip COPY_ENABLED to bring it back.
  const showCopy = COPY_ENABLED && !!msg.text && !msg.streaming;

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(msg.text);
      setCopied(true);
    } catch {
      // Clipboard blocked (insecure origin or denied permission): the text is still selectable by hand.
    }
  };

  const handleThumb = (value: boolean) => {
    setHelpful(value);
    setFeedbackState('form');
  };

  const handleSubmit = async () => {
    if (helpful === null || !conversationId) return;
    await submitFeedback({ conversationId, messageId: msg.id, helpful, comment: comment || null });
    setFeedbackState('submitted');
  };

  const handleCancel = () => {
    setFeedbackState('idle');
    setHelpful(null);
    setComment('');
  };
  return (
  <div className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
    <div className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
      msg.role === 'user'
        ? 'bg-steel text-white whitespace-pre-wrap rounded-[18px] rounded-br-[6px] selection:bg-on-accent selection:text-steel'
        : 'bg-ui-raised border border-ui-border text-ui-text rounded-[18px] rounded-bl-[6px]'
    }`}>
      {msg.role === 'assistant' ? (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
            h1: ({ children }) => <h1 className="text-base font-bold mb-2 mt-1">{children}</h1>,
            h2: ({ children }) => <h2 className="text-sm font-bold mb-2 mt-1 text-ui-text">{children}</h2>,
            h3: ({ children }) => <h3 className="text-sm font-semibold mb-1.5 mt-1 text-ui-text">{children}</h3>,
            ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-0.5">{children}</ul>,
            ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-0.5">{children}</ol>,
            li: ({ children }) => <li className="leading-snug">{children}</li>,
            strong: ({ children }) => <strong className="font-semibold text-ui-text">{children}</strong>,
            em: ({ children }) => <em className="italic text-ui-text2">{children}</em>,
            code: ({ children, className }) => {
              const isBlock = className?.startsWith('language-');
              return isBlock
                ? <code className="block bg-ui-subtle rounded px-3 py-2 text-xs font-mono mt-1 mb-2 overflow-x-auto border border-ui-border">{children}</code>
                : <code className="bg-ui-subtle rounded px-1 py-0.5 text-xs font-mono">{children}</code>;
            },
            pre: ({ children }) => <pre className="mb-2">{children}</pre>,
            hr: () => <hr className="border-ui-border my-2" />,
            blockquote: ({ children }) => <blockquote className="border-l-2 border-ui-border3 pl-3 italic text-ui-text2 my-1">{children}</blockquote>,
            a: ({ href, children }) => <a href={href} className="text-accent-text underline underline-offset-2 hover:opacity-80" target="_blank" rel="noopener noreferrer">{children}</a>,
          }}
        >
          {msg.text}
        </ReactMarkdown>
      ) : (
        msg.text
      )}
      {msg.streaming && <span className="inline-block w-0.5 h-4 bg-current ml-0.5 align-middle animate-pulse opacity-70" />}
      {!!msg.files?.length && (
        <div className={`flex flex-wrap gap-1.5 ${msg.text ? 'mt-2' : ''}`}>
          {msg.files.map((f, i) => {
            const isImg = (f.type || '').startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/i.test(f.name || '');
            if (isImg && f.url) {
              return (
                <a key={`${f.url}-${i}`} href={f.url} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition" title={f.name}>
                  <img src={f.url} alt={f.name} className="max-h-40 max-w-[200px] rounded-lg border border-ui-border object-cover" />
                </a>
              );
            }
            const chip = (
              <span className="flex items-center gap-1 max-w-[180px] bg-ui-subtle border border-ui-border rounded-lg px-2 py-1 text-xs">
                <span className="flex-shrink-0">📎</span>
                <span className="truncate">{f.name}</span>
              </span>
            );
            return f.url
              ? <a key={`${f.url}-${i}`} href={f.url} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition">{chip}</a>
              : <span key={`${f.name}-${i}`}>{chip}</span>;
          })}
        </div>
      )}
    </div>
    {(showTime || showCopy) && (
      <div className={`mt-1 flex items-center gap-1 px-1 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
        {showTime && (
          <span className="text-xs text-ui-text2 tabular-nums select-none">
            {formatMessageTime(msg.createdAt!, t('time.yesterday'), i18n.language)}
          </span>
        )}
        {showCopy && (
          <IconButton size="xs" label={copied ? t('chat.copied') : t('chat.copy')} onClick={handleCopy}>
            {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
          </IconButton>
        )}
      </div>
    )}
    {msg.deliveryStatus === 'failed' && (
      <span className="mt-1 px-1 text-xs text-danger flex items-center gap-1">
        {t('chat.messageFailedToSend')}
        {onRetry && (
          <button type="button" onClick={onRetry} className="underline hover:no-underline">
            {t('chat.retry')}
          </button>
        )}
      </span>
    )}
    {showFeedback && (
      <div className="mt-1.5 px-1 max-w-[85%]">
        {feedbackState === 'idle' && (
          <div className="flex items-center gap-2 text-xs text-ui-text2">
            <span>Was this helpful?</span>
            <button
              type="button"
              aria-label="Thumbs up"
              onClick={() => handleThumb(true)}
              className="hover:text-ui-text transition-colors"
            >
              👍
            </button>
            <button
              type="button"
              aria-label="Thumbs down"
              onClick={() => handleThumb(false)}
              className="hover:text-ui-text transition-colors"
            >
              👎
            </button>
          </div>
        )}
        {feedbackState === 'form' && (
          <div className="flex flex-col gap-2 bg-ui-card border border-ui-border rounded-xl px-3 py-2.5 text-sm shadow-sm">
            <div className="flex items-center gap-1.5 text-xs text-ui-text2 font-medium">
              <span>Was this helpful?</span>
              <span
                role="img"
                aria-label={helpful ? 'Thumbs up' : 'Thumbs down'}
                className="text-base"
              >
                {helpful ? '👍' : '👎'}
              </span>
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What did we get right? (optional)"
              rows={2}
              className="w-full resize-none rounded-lg border border-ui-border bg-ui-subtle px-2.5 py-1.5 text-xs text-ui-text placeholder:text-ui-text3 focus:outline-none focus:ring-1 focus:ring-ui-ring"
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={handleCancel}
                className="px-3 py-1 text-xs text-ui-text2 hover:text-ui-text transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center gap-1.5 px-3 py-1 text-xs btn-sheen text-on-primary rounded-lg disabled:opacity-50"
              >
                {submitting ? (
                  <span className="w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                ) : (
                  <span>✉</span>
                )}
                Submit feedback
              </button>
            </div>
          </div>
        )}
        {feedbackState === 'submitted' && (
          <span className="text-xs text-ui-text2">Thanks for your feedback!</span>
        )}
      </div>
    )}
  </div>
  );
};

export default MessageBubble;
